import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { AddressModel, StepIndicatorModel } from '@app/state/models/add-address.model';
import { Observable } from 'rxjs';
import { updateAddress, updateCurrentStep } from '../../../state/actions/add-address.action';
import {
  selectStepIndicatorIndex,
  getUSStates,
  getCountries,
  selectAddAddress
} from '../../../state/selectors/add-address.selector';
import { AemUtil } from '@app/utils/aem.util';
import { ThirdpartyDocumentsAemItem } from '@app/models/aem.model';
import {
  CANCEL_BUTTON,
  CONTINUE_BUTTON,
  ENTER_NON_US_ADDRESS_BUTTON, ENTER_RECIPIENT_INFO_FORM_CONTAINER,
  ENTER_US_ADDRESS_BUTTON,
  NAME_ADDRESS_LINE_COUNT_ERROR_MESSAGE,
  TPQ_EDIT_INTERNAL_ONLY,
} from '@app/constants/aem.constant';
import { AllowedElements } from '@app/models/thirdparty-documents.model';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-add-address',
  templateUrl: './add-address.component.html',
  styleUrls: ['./add-address.component.scss'],
  standalone: false,
})
export class AddAddressComponent implements OnInit, OnDestroy {
  errorMessages = {
    nameRequired: '',
    namePattern: '',
    nameMaxLength: 'Cannot exceed 30 characters for name field',
    addressRequired: '',
    addressPattern: '',
    addressMaxLength: 'Cannot exceed 30 characters for address field',
    cityRequired: '',
    cityPattern: '',
    zipCodePattern: '',
    countryRequired: '',
  };
  content = {
    ADD_ADDRESS: '',
    HINT_TEXT: '',
    EDIT_ADDRESS: '',
    STEP1_INFO: '',
    STEP1_NON_US_ADDR: '',
    STEP1_US_ADDR: '',
    ADDR_LABEL: '',
    NAME_Label: '',
    ADDR_LINE2_LABEL: '',
    CITY_LABEL: '',
    ZIP_CODE_LABEL: '',
    COUNTRY_LABEL: '',
    SYMBOL: '',
    CHOOSE_ONE: '',
    STATE_LABEL: '',
    COUNTINUE_LABEL: '',
    CANCEL_LABEL: '',
  };
  currentStepIndicatorIndex$: Observable<number>;
  showUsAddress: boolean;
  selectedCountry: string;
  selectedAccounts: any;
  formFields: any;
  title: string;
  numberNameLines = 1;
  showLineCountError = false;
  lineCountErrorMessage = 'Your Duplicate Statement Name and Address cannot have more than five Name and Street Address lines. Please revise the Name and Street Address to contain no more than five total lines.';
  maxLengthHit: Record<string, boolean> = {
    firstname: false, nameLine2: false, nameLine3: false,
    nameLine4: false, address1: false, address2: false,
  };
  private maxLengthTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  isClientOnlyAction = false;
  editInternalOnly = '';
  @Input() thirdpartyDocumentsAemContent: ThirdpartyDocumentsAemItem;
  @ViewChild('addressForm') public addressform: NgForm;
  private readonly destroyRef = inject(DestroyRef);

  myZipMask = {
    mask: 'VALUE',
    lazy: false,
    blocks: {
      VALUE: {
        mask: '00000-0000',
      },
    },
  };
  usStateList$: Observable<AllowedElements[]>;
  countryList$: Observable<AllowedElements[]>;
  address$: Observable<AddressModel>;

  constructor(private _router: Router,
    private store: Store<StepIndicatorModel>) {
    this.usStateList$ = this.store.select(getUSStates);
    this.countryList$ = this.store.select(getCountries);
    this.address$ = this.store.pipe(select(selectAddAddress));
  }

  ngOnInit(): void {
    this.getAemContent();
    this.getClientOnlyFlag();
    this.currentStepIndicatorIndex$ = this.store.pipe(select(selectStepIndicatorIndex));
    this.selectedCountry = '';
    this.selectedAccounts = {};
    this.title = this.content.ADD_ADDRESS;
    this.address$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.showUsAddress = !data.foreignAddress;
      this.selectedAccounts = data.associatedAccounts;
      this.numberNameLines = data.numberNameLines || 1;
      this.title = data.associatedAccounts?.length === 0 ? this.content.ADD_ADDRESS : this.content.EDIT_ADDRESS;
      this.selectedCountry = data.foreignCountryCode?.trim().toUpperCase() + ',' + data.foreignCountryName?.trim().toUpperCase();
      setTimeout(() => {
        this.addressform.form.patchValue({
          firstname: data.name,
          nameLine2: data.nameLine2 || '',
          nameLine3: data.nameLine3 || '',
          nameLine4: data.nameLine4 || '',
          address1: data.addressLine1,
          address2: data.addressLine2,
          city: data.city,
          state: data.state,
          zipcode: data.zipCode + '' + data.zipCodePlusFour,
          country: data.foreignCountryCode?.trim().toUpperCase() + ',' + data.foreignCountryName?.trim().toUpperCase(),
          associatedAccounts: data.associatedAccounts,
        });
      });
    });
  }

  getClientOnlyFlag() {
    if (environment.internal) {
      this.isClientOnlyAction = sessionStorage.getItem('oktaFlag') !== 'true';
    }
  }

  addUserAddress(addressForm: NgForm) {
    if (this.isClientOnlyAction) return;
    this.checkLineCount(addressForm);
    if (this.showUsAddress) {
      const zipCtrl = addressForm.controls['zipcode'];
      const rawZip = (addressForm.value.zipcode || '').replace(/\D/g, '');
      if (zipCtrl) {
        const currentErrors = zipCtrl.errors || {};
        if (rawZip.length > 0 && rawZip.length !== 5 && rawZip.length !== 9) {
          zipCtrl.setErrors({ ...currentErrors, pattern: true });
        } else if (currentErrors['pattern']) {
          const { pattern: _p, ...remainingErrors } = currentErrors;
          zipCtrl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
        }
      }
    }
    if (this.showLineCountError || !addressForm.valid) {
      if (this.showLineCountError) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setTimeout(() => {
          const firstInvalid = document.querySelector('input.ng-invalid, select.ng-invalid');
          if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
      return;
    }

    const userAddress = addressForm.value;
    if (this.showUsAddress) {
      userAddress.zipCodePlusFour = addressForm.value.zipcode.substr(5);
      userAddress.zipcode = addressForm.value.zipcode.substr(0, 5);
    }
    this.store.dispatch(updateCurrentStep({ currentStepIndex: 2 }));

    const addressPayload: AddressModel = {
      numberNameLines: [userAddress.firstname, userAddress.nameLine2, userAddress.nameLine3, userAddress.nameLine4]
        .filter(v => v && v.trim()).length || 1,
      name: userAddress.firstname,
      nameLine2: userAddress.nameLine2 || '',
      nameLine3: userAddress.nameLine3 || '',
      nameLine4: userAddress.nameLine4 || '',
      addressLine1: userAddress.address1,
      addressLine2: userAddress.address2,
      city: userAddress.city,
      state: this.showUsAddress ? userAddress.state : null,
      zipCode: this.showUsAddress ? userAddress.zipcode : '',
      zipCodePlusFour: this.showUsAddress ? userAddress.zipCodePlusFour : '',
      foreignAddress: !this.showUsAddress,
      foreignCountryCode: !this.showUsAddress ? addressForm.value?.country?.split(',')[0] : '',
      foreignCountryName: !this.showUsAddress ? addressForm.value?.country?.split(',')[1] : '',
      associatedAccounts: this.selectedAccounts,
    };

    this.store.dispatch(updateAddress({ address: addressPayload }));
  }

  goToPage(route: string) {
    this.store.dispatch(
      updateAddress({
        address: {
          numberNameLines: 1,
          name: '',
          nameLine2: '',
          nameLine3: '',
          nameLine4: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: null,
          foreignAddress: false,
          foreignCountryCode: '',
          foreignCountryName: '',
          associatedAccounts: [],
        },
      }),
    );
    this._router.navigate([route]);
  }

  addNameLine() {
    if (this.numberNameLines < 4) {
      this.numberNameLines++;
    }
  }

  removeNameLine(lineNumber: number) {
    const keys = ['nameLine2', 'nameLine3', 'nameLine4'];
    const v = this.addressform.value;
    const values = keys.map(k => v[k] || '');
    values.splice(lineNumber - 2, 1);
    values.push('');
    const patch: Record<string, string> = {};
    keys.forEach((k, i) => patch[k] = values[i]);
    this.addressform.form.patchValue(patch);
    this.numberNameLines--;
    this.showLineCountError = false;
  }

  checkLineCount(addressForm: NgForm) {
    const formValues = addressForm.value;
    const filledNameLines = [formValues.firstname, formValues.nameLine2, formValues.nameLine3, formValues.nameLine4]
      .filter(v => v && v.trim()).length;
    const filledAddrLines = [formValues.address1, formValues.address2]
      .filter(v => v && v.trim()).length;
    this.showLineCountError = (filledNameLines + filledAddrLines) > 5;
  }

  checkValid(code: any) {
    return code;
  }

  private getAemContent() {
    this.editInternalOnly = AemUtil.getErrorMessagesFromMessages(this.thirdpartyDocumentsAemContent.messages, TPQ_EDIT_INTERNAL_ONLY);
    const lineCountMsg = this.thirdpartyDocumentsAemContent?.messages?.find(m => m._path?.includes(NAME_ADDRESS_LINE_COUNT_ERROR_MESSAGE));
    if (lineCountMsg?.body) this.lineCountErrorMessage = AemUtil.getHtmlTextFromBodyChild(lineCountMsg.body);
    this.formFields = AemUtil.getFormFieldsFromNestedForm(this.thirdpartyDocumentsAemContent, ENTER_RECIPIENT_INFO_FORM_CONTAINER);
    if (this.formFields) {
      this.content.NAME_Label = this.formFields[0]?.labelText;
      this.content.ADDR_LABEL = this.formFields[1]?.labelText;
      this.content.HINT_TEXT = this.formFields[1]?.hintText;
      this.content.ADDR_LINE2_LABEL = this.formFields[2]?.labelText;
      this.content.CITY_LABEL = this.formFields[3]?.labelText;
      this.content.ZIP_CODE_LABEL = this.formFields[5]?.labelText;
      this.content.STATE_LABEL = this.formFields[4]?.labelText;
      this.content.CHOOSE_ONE = this.formFields[4]?.placeholder;
      this.content.COUNTRY_LABEL = this.formFields[6]?.labelText;
      this.errorMessages.countryRequired = this.formFields[6]?.primaryErrorMessage;
      this.errorMessages.nameRequired = this.formFields[0]?.primaryErrorMessage;
      this.errorMessages.namePattern = this.formFields[0]?.secondaryErrorMessage;
      this.errorMessages.cityRequired = this.formFields[3]?.primaryErrorMessage;
      this.errorMessages.cityPattern = this.formFields[3]?.secondaryErrorMessage;
      this.errorMessages.zipCodePattern = this.formFields[5]?.primaryErrorMessage;
      this.errorMessages.addressRequired = this.formFields[1]?.primaryErrorMessage;
      this.errorMessages.addressPattern = this.formFields[1]?.secondaryErrorMessage;
    }
    const addAddress = AemUtil.getFormAddAddress(this.thirdpartyDocumentsAemContent);
    this.content.ADD_ADDRESS = addAddress?.heading ?? '';
    this.content.STEP1_INFO = addAddress?.html ?? '';
    this.content.EDIT_ADDRESS = addAddress?.additionalHeadings ?? '';
    this.content.STEP1_NON_US_ADDR = AemUtil.getSecondaryCtaLabel(this.thirdpartyDocumentsAemContent, ENTER_NON_US_ADDRESS_BUTTON);
    this.content.STEP1_US_ADDR = AemUtil.getSecondaryCtaLabel(this.thirdpartyDocumentsAemContent, ENTER_US_ADDRESS_BUTTON);
    this.content.COUNTINUE_LABEL = AemUtil.getPrimaryCtaLabel(this.thirdpartyDocumentsAemContent, CONTINUE_BUTTON);
    this.content.CANCEL_LABEL = AemUtil.getPrimaryCtaLabel(this.thirdpartyDocumentsAemContent, CANCEL_BUTTON);
  }

  getNameError(firstname: any): string {
    if (firstname.errors?.required) return this.errorMessages.nameRequired;
    if (firstname.errors?.maxlength) return this.errorMessages.nameMaxLength;
    if (firstname.errors?.pattern) return this.errorMessages.namePattern;
    return this.errorMessages.namePattern;
  }

  getAddressError(address1: any): string {
    if (address1.errors?.required) return this.errorMessages.addressRequired;
    if (address1.errors?.maxlength) return this.errorMessages.addressMaxLength;
    if (address1.errors?.pattern) return this.errorMessages.addressPattern;
    return this.errorMessages.addressPattern;
  }

  ngOnDestroy(): void {
    Object.values(this.maxLengthTimers).forEach(t => clearTimeout(t));
  }

  onPaste(event: ClipboardEvent, field: string): void {
    const input = event.target as HTMLInputElement;
    const pasted = event.clipboardData?.getData('text') ?? '';
    const selectionLength = (input.selectionEnd ?? 0) - (input.selectionStart ?? 0);
    const resultingLength = input.value.length - selectionLength + pasted.length;
    if (resultingLength > 30) {
      this.maxLengthHit[field] = true;
      clearTimeout(this.maxLengthTimers[field]);
      this.maxLengthTimers[field] = setTimeout(() => {
        this.maxLengthHit[field] = false;
      }, 2000);
    }
  }

  onDrop(event: DragEvent, field: string): void {
    const input = event.target as HTMLInputElement;
    const dropped = event.dataTransfer?.getData('text') ?? '';
    const selectionLength = (input.selectionEnd ?? 0) - (input.selectionStart ?? 0);
    const resultingLength = input.value.length - selectionLength + dropped.length;
    if (resultingLength > 30) {
      this.maxLengthHit[field] = true;
      clearTimeout(this.maxLengthTimers[field]);
      this.maxLengthTimers[field] = setTimeout(() => {
        this.maxLengthHit[field] = false;
      }, 2000);
    }
  }

  onKeydown(event: KeyboardEvent, field: string): void {
    const input = event.target as HTMLInputElement;
    // keydown fires before the browser updates the value, so Backspace/Delete
    // must be caught here explicitly — by the time keydown runs, value.length
    // is still at 30, which would fool the length check below.
    if (event.key === 'Backspace' || event.key === 'Delete') {
      clearTimeout(this.maxLengthTimers[field]);
      this.maxLengthHit[field] = false;
      return;
    }
    const isNavigationKey = event.key.length > 1;
    const isModifiedKey = event.ctrlKey || event.metaKey || event.altKey;
    if (!isNavigationKey && !isModifiedKey && input.value.length >= 30) {
      this.maxLengthHit[field] = true;
      clearTimeout(this.maxLengthTimers[field]);
      this.maxLengthTimers[field] = setTimeout(() => {
        this.maxLengthHit[field] = false;
      }, 2000);
    } else if (input.value.length < 30) {
      this.maxLengthHit[field] = false;
    }
  }
}
